import React, { useState, useEffect } from 'react';
import { MessageSquare, Tag, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface SMSStepConfigProps {
  config: any;
  onChange: (config: any) => void;
}

interface SMSTemplate {
  id: string;
  name: string;
  message: string;
}

const MERGE_TAGS = [
  { tag: '{{first_name}}', label: 'First Name' },
  { tag: '{{last_name}}', label: 'Last Name' },
  { tag: '{{company_name}}', label: 'Company Name' },
  { tag: '{{phone}}', label: 'Phone' },
  { tag: '{{service_type}}', label: 'Service Type' },
  { tag: '{{opt_out}}', label: 'Opt-out Text' },
];

const MAX_SMS_LENGTH = 160;
const MAX_SMS_SEGMENTS = 3;

export const SMSStepConfig: React.FC<SMSStepConfigProps> = ({ config, onChange }) => {
  const [templates, setTemplates] = useState<SMSTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<SMSTemplate | null>(null);
  const [messageLength, setMessageLength] = useState(0);
  const [segments, setSegments] = useState(1);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    if (config.template_id && templates.length > 0) {
      const template = templates.find(t => t.id === config.template_id);
      setSelectedTemplate(template || null);
      // If message is not already set, populate from template
      if (template && !config.message) {
        onChange({
          ...config,
          message: template.message || '',
        });
      }
    }
  }, [config.template_id, templates]);

  useEffect(() => {
    const message = config.message || '';
    setMessageLength(message.length);
    setSegments(Math.ceil(message.length / MAX_SMS_LENGTH) || 1);
  }, [config.message]);

  const loadTemplates = async () => {
    try {
      // Query from sms_templates table (the actual table used by the system)
      const { data, error } = await supabase
        .from('sms_templates')
        .select('id, name, message')
        .order('name');

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    onChange({
      ...config,
      template_id: templateId,
      message: template?.message || config.message || '',
    });
  };

  const insertMergeTag = (tag: string) => {
    const currentValue = config.message || '';
    onChange({
      ...config,
      message: currentValue + tag,
    });
  };

  const getSegmentWarning = () => {
    if (segments > MAX_SMS_SEGMENTS) {
      return {
        type: 'error',
        message: `Message is too long (${segments} segments). Maximum ${MAX_SMS_SEGMENTS} segments allowed.`,
      };
    }
    if (segments > 1) {
      return {
        type: 'warning',
        message: `Message will be sent as ${segments} segments and may incur additional charges.`,
      };
    }
    return null;
  };

  const warning = getSegmentWarning();

  return (
    <div className="space-y-6">
      {/* Template Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          SMS Template *
        </label>
        {loading ? (
          <div className="text-sm text-gray-500">Loading templates...</div>
        ) : (
          <select
            value={config.template_id || ''}
            onChange={(e) => handleTemplateChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select a template...</option>
            {templates.map(template => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        )}
        {templates.length === 0 && !loading && (
          <p className="mt-2 text-sm text-amber-600">
            No SMS templates found. Create templates in the Templates page first.
          </p>
        )}
      </div>

      {/* Message */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Message *
          </label>
          <div className="flex gap-1">
            {MERGE_TAGS.slice(0, 3).map(({ tag, label }) => (
              <button
                key={tag}
                type="button"
                onClick={() => insertMergeTag(tag)}
                className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                title={`Insert ${label}`}
              >
                <Tag className="w-3 h-3" />
              </button>
            ))}
          </div>
        </div>
        <textarea
          value={config.message || ''}
          onChange={(e) => onChange({ ...config, message: e.target.value })}
          placeholder="Enter SMS message..."
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
        <div className="mt-1 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Use merge tags like {MERGE_TAGS.slice(0, 2).map(t => t.tag).join(', ')}
          </p>
          <p className={`text-xs ${
            segments > MAX_SMS_SEGMENTS ? 'text-red-600' :
            segments > 1 ? 'text-amber-600' :
            'text-gray-500'
          }`}>
            {messageLength} characters • {segments} segment{segments !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Warning Message */}
      {warning && (
        <div className={`flex items-start gap-2 p-3 rounded-lg ${
          warning.type === 'error' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'
        }`}>
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <p className="text-sm">{warning.message}</p>
        </div>
      )}

      {/* From Number */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          From Number
        </label>
        <input
          type="tel"
          value={config.from_number || ''}
          onChange={(e) => onChange({ ...config, from_number: e.target.value })}
          placeholder="+1 (555) 123-4567"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="mt-1 text-xs text-gray-500">
          Leave empty to use default company SMS number
        </p>
      </div>

      {/* SMS Settings */}
      <div className="space-y-3">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.include_opt_out || true}
            onChange={(e) => onChange({ ...config, include_opt_out: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Include opt-out instructions</span>
        </label>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.check_opt_in_status || true}
            onChange={(e) => onChange({ ...config, check_opt_in_status: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Only send to opted-in contacts</span>
        </label>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={config.shorten_urls || false}
            onChange={(e) => onChange({ ...config, shorten_urls: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-700">Automatically shorten URLs</span>
        </label>
      </div>

      {/* Message Preview */}
      {config.message && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Message Preview</h4>
          <div className="bg-white rounded-lg p-3 border border-gray-200">
            <div className="flex items-start gap-3">
              <MessageSquare className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                  {config.message
                    .replace(/{{first_name}}/g, 'John')
                    .replace(/{{last_name}}/g, 'Doe')
                    .replace(/{{company_name}}/g, 'Example Company')
                    .replace(/{{phone}}/g, '(555) 123-4567')
                    .replace(/{{service_type}}/g, 'HVAC Maintenance')
                    .replace(/{{opt_out}}/g, 'Reply STOP to opt-out')}
                </p>
                {config.include_opt_out && !config.message.includes('{{opt_out}}') && (
                  <p className="text-xs text-gray-500 mt-2 pt-2 border-t">
                    Reply STOP to opt-out
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Available Merge Tags */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Available Merge Tags</h4>
        <div className="grid grid-cols-2 gap-2">
          {MERGE_TAGS.map(({ tag, label }) => (
            <div key={tag} className="text-xs">
              <code className="bg-white px-2 py-1 rounded border border-gray-200">
                {tag}
              </code>
              <span className="ml-2 text-gray-600">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Compliance Notice */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">SMS Compliance</h4>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>• Only send to contacts who have opted in to receive SMS</li>
          <li>• Include opt-out instructions in all messages</li>
          <li>• Respect quiet hours (typically 8am-9pm local time)</li>
          <li>• Messages over 160 characters will be split into multiple segments</li>
        </ul>
      </div>
    </div>
  );
};
